import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import {
  useCreateContract,
  useUpdateContract,
  CreateContractValidation,
  UpdateContractValidation,
} from "@/lib/api/otc";
import { UpdateContractInfo } from "@/section/otc/OtcBearState";

interface UseOtcContractFormHandlersProps {
  refetchContracts: () => void;
  onCloseModal: () => void;
}

export function useOtcContractFormHandlers({
  refetchContracts,
  onCloseModal,
}: UseOtcContractFormHandlersProps) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const createContract = useCreateContract();
  const updateContract = useUpdateContract();

  const onSubmitForm = async (
    formData: UpdateContractInfo,
    isUpdateForm: boolean,
  ): Promise<boolean> => {
    if (isSaving) return false;

    setIsSaving(true);

    // The V2 form handles validation and data transformation (including
    // canola derived fields) before calling onSubmit, so formData is
    // already API-ready.
    const data = JSON.parse(JSON.stringify(formData));

    const saveResult = await (!!isUpdateForm
      ? updateContract(data as typeof UpdateContractValidation._type)
      : createContract(data as typeof CreateContractValidation._type));

    setIsSaving(false);

    if (!saveResult.success) {
      toast({
        title: isUpdateForm
          ? "Failed to update contract"
          : "Failed to create contract",
        description: "",
      });
    } else {
      onCloseModal();
      refetchContracts();
      toast({
        title: isUpdateForm ? "Updated" : "Created",
        description: "",
      });
    }

    return true;
  };

  return {
    onSubmitForm,
    isSaving,
    createContract,
    updateContract,
  };
}

